/* `gdtile://` — chart tiles, cache-first, out of IndexedDB.
 *
 * This is the half of the tile path that used to live in the service worker.
 * See `tilestore.ts` for why it moved; the short version is that a service
 * worker cannot register over plain http, so on the boat's own network there
 * was no chart cache at all.
 *
 * ═══ WHY A PROTOCOL AND NOT A FETCH WRAPPER ═══
 *
 * MapLibre owns tile loading — it decides which tiles, when, and how many at
 * once, and it aborts the ones a pan moved past. A custom protocol is the only
 * hook that sits INSIDE that machinery, so the cache is consulted before a
 * request is issued rather than after. A cached tile then costs no HTTP request
 * at all, which is the point: measured on the boat, six connections to the pi
 * were being spent re-fetching chart the device already had, and tiles the pi
 * held on disk queued behind them (M13).
 *
 * ═══ THE URL IS OURS, WHICH RETIRES A FOOTGUN ═══
 *
 * The service worker had to recognise tiles by SHAPE — a regex over the path,
 * carefully written so `/signalk/chart-cache/stats` did not match, because a
 * prefix test would have cached the stats response forever and frozen the SETUP
 * figures at whatever they were the first time the app was opened.
 *
 * Here we mint the URL, so there is nothing to recognise. `gdtile://` is a
 * chart tile because nothing else uses that scheme.
 */

import { chartTiles } from './style.ts';
import * as store from './tilestore.ts';
import { pointInBbox, type Bbox } from '../lib/geo.ts';
import { tileProgress } from './tile-progress.svelte.ts';

export const GD_TILE = 'gdtile';
/** A dead proxy must not keep MapLibre waiting forever. */
export const TILE_FETCH_TIMEOUT_MS = 25_000;

const retryGenerations = new Map<string, number>();
const chartConfigs = new Map<string, { useCache: boolean; generation: number }>();

/**
 * `gdtile://{chart}/{z}/{x}/{y}?g={generation}&c={0|1}`
 *
 * THE CHART ID IS IN THE URL, and it has to be. There is one `chart-cache`
 * source but a proxy can front several charts — the hosted one serves CHS and
 * NOAA off the same deployment, keyed by path — and without the id here every
 * chart would be cached under the same key and served the wrong country's
 * squares. It is also part of the IndexedDB key, for exactly the same reason.
 *
 * Defaulted to `chs-enc` so a caller that predates multiple charts is unchanged.
 */
export function chartTileUrl(useCache: boolean, generation: number, chart = 'chs-enc'): string {
  chartConfigs.set(chart, { useCache, generation });
  const effectiveGeneration = generation + (retryGenerations.get(chart) ?? 0);
  return `${GD_TILE}://${chart}/{z}/{x}/{y}?g=${effectiveGeneration}&c=${useCache ? 1 : 0}`;
}

interface Parsed {
  chart: string;
  z: number;
  x: number;
  y: number;
  generation: number;
  useCache: boolean;
}

export function parseTileUrl(url: string): Parsed | null {
  const m = /^gdtile:\/\/([a-z0-9-]+)\/(\d+)\/(\d+)\/(\d+)\?(.*)$/.exec(url);
  if (!m) return null;
  const q = new URLSearchParams(m[5]);
  return {
    chart: m[1]!,
    z: +m[2]!,
    x: +m[3]!,
    y: +m[4]!,
    generation: Number(q.get('g') ?? 0),
    useCache: q.get('c') === '1'
  };
}

/* The cache key carries the GENERATION. Bumping it in the plugin changes every
 * key, which is the one mechanism there is for expiring charts when CHS
 * reissues them — the same reason it is in the HTTP URL. It also carries which
 * upstream answered, so tiles fetched direct from CHS during an outage are not
 * confused with the pi's. */
const keyFor = (p: Parsed) =>
  `${p.useCache ? 'c' : 'd'}/${p.chart}/${p.generation}/${p.z}/${p.x}/${p.y}`;

type AddProtocol = (
  scheme: string,
  handler: (
    params: { url: string },
    abort?: AbortController
  ) => Promise<{ data: ArrayBuffer; cacheControl?: string; expires?: string }>
) => void;

/** Counters for the debug panel — the cache is invisible otherwise. */
export const tileCacheStats = { hit: 0, miss: 0, stored: 0, failed: 0 };

export type ChartTileOutcome = 'drawn' | 'no-coverage' | 'failed';

export interface ChartTileOutcomeSummary {
  total: number;
  drawn: number;
  noCoverage: number;
  failed: number;
}

export interface ChartTileStatus {
  chart: string;
  z: number;
  x: number;
  y: number;
  outcome: ChartTileOutcome;
  at: number;
}

const tileStatuses = new Map<string, ChartTileStatus>();
const tileStatusListeners = new Set<() => void>();
const statusKey = (p: Pick<Parsed, 'chart' | 'z' | 'x' | 'y'>) =>
  `${p.chart}/${p.z}/${p.x}/${p.y}`;

function recordTileStatus(p: Parsed, outcome: ChartTileOutcome): void {
  tileStatuses.set(statusKey(p), { chart: p.chart, z: p.z, x: p.x, y: p.y, outcome, at: Date.now() });
  tileProgress.settle();
  for (const listener of tileStatusListeners) listener();
}

/** Clear diagnosed outcomes (failed and no-coverage) and force fresh tile URLs. */
export async function retryFailedChartTiles(
  map: { getSource(id: string): unknown },
  charts: readonly string[],
  reregister?: (charts: readonly string[]) => void | Promise<void>
): Promise<void> {
  tileProgress.reset();
  for (const chart of charts) {
    for (const [key, status] of tileStatuses) {
      if (
        status.chart === chart
        && (status.outcome === 'failed' || status.outcome === 'no-coverage')
      ) tileStatuses.delete(key);
    }
    retryGenerations.set(chart, (retryGenerations.get(chart) ?? 0) + 1);
    const config = chartConfigs.get(chart);
    const source = map.getSource(chart) as { setTiles?: (tiles: string[]) => void } | undefined;
    if (config && source?.setTiles) {
      source.setTiles([chartTileUrl(config.useCache, config.generation, chart)]);
    }
  }
  /* setTiles only refreshes a source which already exists. A zero-request
   * startup can have no source at all, so retry must also run each chart
   * module's normal remove/add path. */
  await reregister?.(charts);
  for (const listener of tileStatusListeners) listener();
}

/** Re-run `listener` when a chart square finishes or fails. */
export function onChartTileStatus(listener: () => void): () => void {
  tileStatusListeners.add(listener);
  return () => tileStatusListeners.delete(listener);
}

/**
 * Outcomes for the chart squares intersecting the current view. Unknown
 * squares are included in the total: loading is not evidence of no coverage.
 */
export function visibleChartTileOutcomes(
  chart: string,
  bounds: { west: number; south: number; east: number; north: number },
  zoom: number
): ChartTileOutcomeSummary {
  const z = Math.max(0, Math.floor(zoom));
  const n = 2 ** z;
  const lonX = (lon: number) => Math.floor(((lon + 180) / 360) * n);
  const latY = (lat: number) => {
    const limited = Math.max(-85.05112878, Math.min(85.05112878, lat));
    const r = (limited * Math.PI) / 180;
    return Math.floor(((1 - Math.asinh(Math.tan(r)) / Math.PI) / 2) * n);
  };
  const minX = Math.max(0, lonX(bounds.west));
  const maxX = Math.min(n - 1, lonX(bounds.east));
  const minY = Math.max(0, latY(bounds.north));
  const maxY = Math.min(n - 1, latY(bounds.south));
  const out = { total: 0, drawn: 0, noCoverage: 0, failed: 0 };
  for (let x = minX; x <= maxX; x++) {
    for (let y = minY; y <= maxY; y++) {
      out.total++;
      const outcome = tileStatuses.get(`${chart}/${z}/${x}/${y}`)?.outcome;
      if (outcome === 'drawn') out.drawn++;
      else if (outcome === 'no-coverage') out.noCoverage++;
      else if (outcome === 'failed') out.failed++;
    }
  }
  return out;
}

/** Unknown/loading squares remain in `total`, so transient failures cannot
 * become an outage notice before they are a majority of the visible grid. */
export function isChartLayerOutage(
  visible: boolean,
  outcomes: { total: number; failed: number }
): boolean {
  return visible && outcomes.total > 0 && outcomes.failed > outcomes.total / 2;
}

type CoverageOutcomes = {
  total: number;
  drawn: number;
  noCoverage: number;
  failed: number;
};

type OfficialChartCoverage = {
  outcomes: CoverageOutcomes;
  coverage?: Bbox;
};

/** Whether one official chart layer is absent at the view centre, according to
 * either its declared coverage or the definitive outcomes of settled tiles.
 * Drawn ink wins; failed and unknown/loading squares are not evidence. */
export function isChartLayerUncovered(options: {
  center: { lon: number; lat: number };
  outcomes: CoverageOutcomes;
  coverage?: Bbox;
}): boolean {
  if (options.outcomes.drawn > 0) return false;
  if (options.coverage && !pointInBbox(options.coverage, options.center.lon, options.center.lat)) {
    return true;
  }
  const settled = options.outcomes.drawn + options.outcomes.noCoverage;
  return settled > 0 && options.outcomes.noCoverage > settled / 2;
}

/** Both official chart layers are absent at the view centre, according to
 * either their declared coverage or the definitive outcomes of settled tiles.
 * Failed and unknown/loading squares are not evidence either way. */
export function isNoOfficialChartCoverage(options: {
  center: { lon: number; lat: number };
  chs: OfficialChartCoverage;
  noaa: OfficialChartCoverage;
}): boolean {
  return isChartLayerUncovered({ center: options.center, ...options.chs })
    && isChartLayerUncovered({ center: options.center, ...options.noaa });
}

/**
 * Call once, with THE SAME MapLibre module the map was created from.
 *
 * A second `import('maplibre-gl')` under Vite is a different module object with
 * its own empty protocol registry, and the map would never see this handler —
 * no error, no warning, just tiles that never arrive. The same trap
 * `terrain-protocol.ts` documents, and it will not announce itself.
 */
export function registerTileProtocol(
  ml: { addProtocol: AddProtocol },
  timeoutMs = TILE_FETCH_TIMEOUT_MS
): void {
  ml.addProtocol(GD_TILE, async ({ url }, abort) => {
    const p = parseTileUrl(url);
    if (!p) throw new Error(`not a chart tile url: ${url}`);
    tileProgress.request();

    const k = keyFor(p);
    const hit = await store.get(k);
    if (hit) {
      tileCacheStats.hit++;
      recordTileStatus(p, 'drawn');
      return { data: hit.buf };
    }
    tileCacheStats.miss++;

    const real = chartTiles(p.useCache, p.generation, p.chart)
      .replace('{z}', String(p.z))
      .replace('{x}', String(p.x))
      .replace('{y}', String(p.y));

    const controller = new AbortController();
    let timedOut = false;
    const abortFromMap = () => controller.abort(abort?.signal.reason);
    if (abort?.signal.aborted) abortFromMap();
    else abort?.signal.addEventListener('abort', abortFromMap, { once: true });
    const timeout = setTimeout(() => {
      timedOut = true;
      controller.abort(new Error(`tile fetch timed out after ${timeoutMs}ms`));
    }, timeoutMs);
    const cleanup = () => {
      clearTimeout(timeout);
      abort?.signal.removeEventListener('abort', abortFromMap);
    };

    let r: Response;
    try {
      r = await fetch(real, { signal: controller.signal });
    } catch (error) {
      if (timedOut) {
        tileCacheStats.failed++;
        recordTileStatus(p, 'failed');
      }
      cleanup();
      throw error;
    }

    /* ═══ ONLY A REAL TILE IS KEPT, AND ONLY A REAL TILE IS RETURNED ═══
     *
     * The pi answers 404 for "CHS has no chart on this square" and 503 for
     * "no internet and I have never seen it" — and since M13, 503 for "still
     * fetching, ask again shortly". All three must reach MapLibre as a FAILED
     * tile, because the chart layer treats a tile that loads as proof the WMS
     * is covering the view and stands the pi's own offline charts down.
     * Returning an empty buffer with a success would blank the chart.
     *
     * Throwing is how a protocol handler says that. MapLibre marks the tile
     * errored and keeps showing the coarser parent, which is exactly right for
     * the 503-still-fetching case: the next request will be a hit. */
    if (!r.ok) {
      cleanup();
      tileCacheStats.failed++;
      recordTileStatus(p, r.status === 404 ? 'no-coverage' : 'failed');
      throw new Error(`tile ${p.z}/${p.x}/${p.y}: HTTP ${r.status}`);
    }

    let buf: ArrayBuffer;
    try {
      buf = await r.arrayBuffer();
    } catch (error) {
      if (timedOut) {
        tileCacheStats.failed++;
        recordTileStatus(p, 'failed');
      }
      cleanup();
      throw error;
    }
    cleanup();
    if (buf.byteLength === 0) {
      tileCacheStats.failed++;
      recordTileStatus(p, 'failed');
      throw new Error(`tile ${p.z}/${p.x}/${p.y}: empty`);
    }

    /* Stored WITHOUT awaiting — the tile is already in hand and the map should
     * get it now. A slow write must not delay a draw. */
    void store.put(k, buf.slice(0), r.headers.get('content-type') ?? 'image/png');
    tileCacheStats.stored++;
    recordTileStatus(p, 'drawn');
    return { data: buf };
  });
}
